import { motion } from "framer-motion";
import { DollarSign, Percent, Plus, Trash2 } from "lucide-react";
import React from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Label,
  RequiredLabel,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { NumericInput } from "@/components/ui/numeric-input";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/context/i18n";

import { TProductFormData } from "../../products.schema";

export function DynamicInputList({
  form,
}: {
  form: UseFormReturn<TProductFormData>;
}) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "commission_distribution",
    shouldUnregister: false
  });
  const { t } = useI18n();

  const commissionDistribution = useWatch({
    control: form.control,
    name: "commission_distribution",
  });

  const commissionLifetimeOther = useWatch({
    control: form.control,
    name: "commission_lifetime_other_installments",
  });

  const proLaboreLifetimeOther = useWatch({
    control: form.control,
    name: "pro_labore_lifetime_other_installments",
  });

  const markupValues = useWatch({
    control: form.control,
    name: "commission_distribution",
  })?.map((item: any) => item?.markup) || [];

  const markupModes = useWatch({
    control: form.control,
    name: "commission_distribution",
  })?.map((item: any) => item?.markup_mode) || [];

  const totalAgency = React.useMemo(() => {
    const distribution = commissionDistribution || [];
    const total = distribution.reduce(
      (
        sum: number,
        item: { agency?: number; commission?: number; pro_labore?: number }
      ) => {
        const val = Number(item?.agency || 0);
        return sum + val;
      },
      0
    );
    // Arredonda para 2 casas decimais para evitar problemas de precisão
    return Math.round(total * 100) / 100;
  }, [commissionDistribution, markupValues, markupModes]);

  const totalVitalicio = React.useMemo(() => {
    const commission = Number(commissionLifetimeOther || 0);
    const proLabore = Number(proLaboreLifetimeOther || 0);
    // Arredonda para 2 casas decimais para evitar problemas de precisão
    const total = Math.round((commission + proLabore) * 100) / 100;
    return total;
  }, [commissionLifetimeOther, proLaboreLifetimeOther]);

  const totalMarkupPercentage = React.useMemo(() => {
    const distribution = commissionDistribution || [];
    const total = distribution.reduce(
      (
        sum: number,
        item: { agency?: number; commission?: number; pro_labore?: number; markup?: number; markup_mode?: string }
      ) => {
        const mode = item?.markup_mode || "percentage";
        if (mode === "percentage") {
          const val = Number(item?.markup || 0);
          return sum + val;
        }
        return sum;
      },
      0
    );
    // Arredonda para 2 casas decimais para evitar problemas de precisão
    return Math.round(total * 100) / 100;
  }, [commissionDistribution, markupValues, markupModes]);

  const totalMarkupReal = React.useMemo(() => {
    const distribution = commissionDistribution || [];
    return distribution.reduce(
      (
        sum: number,
        item: { agency?: number; commission?: number; pro_labore?: number; markup?: number; markup_mode?: string }
      ) => {
        const mode = item?.markup_mode || "percentage";
        if (mode === "real_currency") {
          const val = Number(item?.markup || 0);
          return sum + val;
        }
        return sum;
      },
      0
    );
  }, [commissionDistribution, markupValues, markupModes]);
  const addInput = () => {
    append({ agency: 0, commission: 0, pro_labore: 0, markup: 0, markup_mode: "percentage" });
  };

  const removeInput = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-4">
        <motion.div
          key={1}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 1 * 0.1 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.form.fields.agency")}
              </CardTitle>
              <Percent className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalAgency.toLocaleString('pt-BR', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}%
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div
          key={2}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 2 * 0.1 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.form.fields.markup")}
              </CardTitle>
              <Percent className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalMarkupPercentage.toLocaleString('pt-BR', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}%
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div
          key={3}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 3 * 0.1 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.form.fields.markupValue")}
              </CardTitle>
              <DollarSign className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                R$ {(totalMarkupReal / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div
          key={4}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 4 * 0.1 }}
        >
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                {t("products.form.fields.lifetime")}
              </CardTitle>
              <Percent className="w-4 h-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {totalVitalicio.toLocaleString('pt-BR', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}%
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        {fields.map((field, index) => (
          <React.Fragment key={field.id}>
            <Card
              className={index === 0 ? "p-4 col-span-9" : "p-4 col-span-12"}
            >
              <CardTitle className="mb-4">
                {t("products.form.fields.lifetimeCommission")} {index + 1}º
              </CardTitle>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name={`commission_distribution.${index}.agency`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <RequiredLabel
                        htmlFor={`commission_distribution.${index}.agency`}
                        required
                      >
                        {t("products.form.fields.agency")}
                      </RequiredLabel>
                      <FormControl>
                        <NumericInput
                          id={`commission_distribution.${index}.agency`}
                          value={field.value || 0}
                          onChange={field.onChange}
                          className="w-full"
                          placeholder="0,00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`commission_distribution.${index}.commission`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <RequiredLabel
                        htmlFor={`commission_distribution.${index}.commission`}
                        required
                      >
                        {t("products.form.fields.comission")}
                      </RequiredLabel>
                      <FormControl>
                        <NumericInput
                          id={`commission_distribution.${index}.commission`}
                          value={field.value || 0}
                          onChange={field.onChange}
                          className="w-full"
                          placeholder="0,00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`commission_distribution.${index}.pro_labore`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <RequiredLabel
                        htmlFor={`commission_distribution.${index}.pro_labore`}
                        required
                      >
                        {t("products.form.fields.pro_labore")}
                      </RequiredLabel>
                      <FormControl>
                        <NumericInput
                          id={`commission_distribution.${index}.pro_labore`}
                          value={field.value || 0}
                          onChange={field.onChange}
                          className="w-full"
                          placeholder="0,00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`commission_distribution.${index}.markup`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <div className="flex items-center justify-between">
                        <RequiredLabel
                          htmlFor={`commission_distribution.${index}.markup`}
                          required
                        >
                          {form.watch(`commission_distribution.${index}.markup_mode`) === "real_currency"
                            ? t("products.form.fields.markupValue")
                            : t("products.form.fields.markup")
                          }
                        </RequiredLabel>
                        <div className="flex items-center gap-2">
                          <FormField
                            control={form.control}
                            name={`commission_distribution.${index}.markup_mode`}
                            render={({ field: modeField }) => (
                              <FormControl>
                                <div className="flex items-center gap-1">
                                  <Percent className="w-3 h-3 text-muted-foreground" />
                                  <Switch 
                                    checked={modeField.value === "real_currency"}
                                    onCheckedChange={(checked) => {
                                      modeField.onChange(checked ? "real_currency" : "percentage");
                                    }}
                                  />
                                  <DollarSign className="w-3 h-3 text-muted-foreground" />
                                </div>
                              </FormControl>
                            )}
                          />
                        </div>
                      </div>
                      <FormControl>
                        {form.watch(`commission_distribution.${index}.markup_mode`) === "real_currency" ? (
                          <RealCurrencyInput
                            id={`commission_distribution.${index}.markup`}
                            value={field.value || 0}
                            onChange={field.onChange}
                          />
                        ) : (
                          <NumericInput
                            id={`commission_distribution.${index}.markup`}
                            value={field.value || 0}
                            onChange={(value) => {
                              field.onChange(value);
                              form.setValue(`commission_distribution.${index}.markup`, value, {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true
                              });
                            }}
                            placeholder="0,00"
                            className="w-full"
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </Card>
            {index === 0 && (
              <Card className="p-4 col-span-3 self-start h-full">
                <CardTitle>Demais</CardTitle>
                <FormField
                  control={form.control}
                  name={`commission_lifetime_other_installments`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Label htmlFor={`commission_lifetime_other_installments`}>
                        {t("products.form.fields.comission")}
                      </Label>
                      <FormControl>
                        <NumericInput
                          id={`commission_lifetime_other_installments`}
                          value={field.value || 0}
                          onChange={field.onChange}
                          placeholder="0,00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`pro_labore_lifetime_other_installments`}
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Label htmlFor={`pro_labore_lifetime_other_installments`}>
                        {t("products.form.fields.pro_labore")}
                      </Label>
                      <FormControl>
                        <NumericInput
                          id={`pro_labore_lifetime_other_installments`}
                          value={field.value || 0}
                          onChange={field.onChange}
                          placeholder="0,00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Card>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="flex gap-2">
        <Button 
          type="button" 
          variant="outline" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addInput();
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("generic.add")}
        </Button>

        {fields.length > 1 && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => removeInput(fields.length - 1)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t("generic.removeLast")}
          </Button>
        )}
      </div>
    </div>
  );
}
